import {
  type ChangeEvent,
  type KeyboardEvent,
  memo,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from 'react';

import { SearchIcon, XIcon } from '@air/design-icons';
import { IconButton } from '@air/primitive-icon-button';

import { WorkspaceAgentFilterOption } from '~/components/v2/components/workspace/workspace-agent-panel/components/consent/form/WorkspaceAgentFilterOption';
import { type WorkspaceAgentFilterListOption } from '~/components/v2/components/workspace/workspace-agent-panel/utils/toWorkspaceAgentFilterListOptions';

/** The scroll list's `py-1`, kept when scrolling a row into view so the first row never sits flush to the edge. */
const LIST_PAD_Y_PX = 4;

const nextHighlightPosition = (position: number, key: string, length: number) => {
  const direction = key === 'ArrowDown' ? 1 : -1;

  return (position + direction + length) % length;
};

const isFilterListSubmitKey = (event: KeyboardEvent<HTMLInputElement>) =>
  event.key === 'Enter' && !event.metaKey && !event.ctrlKey;

export type WorkspaceAgentFilterListProps = {
  multiple: boolean;
  onQueryChange: (value: string) => void;
  onSelect: (index: number) => void;
  options: readonly WorkspaceAgentFilterListOption[];
  query: string;
  selectedIndexes: readonly number[];
};

/**
 * A combobox: the search field keeps focus, arrow keys move the highlight, Enter picks it. Pick one marks
 * the pick with a check; pick several shows a checkbox per row. Options keep their original index, so
 * picks survive filtering.
 */
export const WorkspaceAgentFilterList = memo(
  ({ multiple, onQueryChange, onSelect, options, query, selectedIndexes }: WorkspaceAgentFilterListProps) => {
    const listId = useId();
    const listRef = useRef<HTMLDivElement>(null);
    const [activeIndex, setActiveIndex] = useState<number | null>(null);
    const [isKeyboardHighlight, setIsKeyboardHighlight] = useState(false);
    const normalizedQuery = query.trim().toLowerCase();
    const visible = useMemo(
      () => options.filter(({ label }) => label.toLowerCase().includes(normalizedQuery)),
      [normalizedQuery, options],
    );
    const followedPosition = visible.findIndex((option) => option.index === activeIndex);
    const highlightedPosition = visible.length === 0 ? -1 : Math.max(0, followedPosition);
    const highlighted = highlightedPosition >= 0 ? visible[highlightedPosition] : undefined;
    const rowDomId = (index: number) => `${listId}-option-${index}`;

    // Scrolls the list only; `scrollIntoView` would also move the panel.
    useEffect(() => {
      const list = listRef.current;
      const row =
        highlighted === undefined
          ? null
          : list?.querySelector<HTMLElement>(`[data-option-index="${highlighted.index}"]`);

      // biome-ignore lint/suspicious/noUnnecessaryConditions: the ref is populated by React on mount; Biome only sees the initial `null`.
      if (!list || !row) {
        return;
      }

      const top = row.offsetTop - LIST_PAD_Y_PX;
      const bottom = row.offsetTop + row.offsetHeight + LIST_PAD_Y_PX;

      if (top < list.scrollTop) {
        list.scrollTop = Math.max(0, top);
      } else if (bottom > list.scrollTop + list.clientHeight) {
        list.scrollTop = bottom - list.clientHeight;
      }
    }, [highlighted]);

    const handleQueryChange = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        onQueryChange(event.target.value);
      },
      [onQueryChange],
    );
    const handleClear = useCallback(() => {
      onQueryChange('');
    }, [onQueryChange]);
    const handleHighlight = useCallback((index: number) => {
      setActiveIndex(index);
    }, []);
    const handleMouseMove = useCallback(() => {
      setIsKeyboardHighlight(false);
    }, []);

    const handleKeyDown = useCallback(
      (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();

          const next = visible[nextHighlightPosition(highlightedPosition, event.key, visible.length)];

          if (next) {
            setIsKeyboardHighlight(true);
            setActiveIndex(next.index);
          }
        } else if (isFilterListSubmitKey(event)) {
          event.preventDefault();

          if (highlighted) {
            onSelect(highlighted.index);
          }
        } else if (event.key === 'Escape' && query.length > 0) {
          event.preventDefault();
          onQueryChange('');
        }
      },
      [highlighted, highlightedPosition, onQueryChange, onSelect, query.length, visible],
    );

    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex h-8 items-center gap-2 rounded-8 border border-gray-5 bg-gray-1 px-2.5 focus-within:border-altitude-9 focus-within:ring-1 focus-within:ring-altitude-9">
          <SearchIcon aria-hidden className="size-3.5 shrink-0 text-gray-9" />
          <input
            aria-activedescendant={highlighted ? rowDomId(highlighted.index) : undefined}
            aria-autocomplete="list"
            aria-controls={listId}
            aria-expanded
            aria-label="Search options"
            className="min-w-0 flex-1 bg-transparent text-12 text-gray-12 leading-4 outline-none placeholder:text-gray-9"
            data-dock-local-escape={query.length > 0 ? '' : undefined}
            onChange={handleQueryChange}
            onKeyDown={handleKeyDown}
            placeholder="Search"
            role="combobox"
            type="text"
            value={query}
          />
          {query.length > 0 ? (
            <IconButton appearance="ghost" color="gray" label="Clear search" onClick={handleClear} size="extra-small">
              <XIcon className="size-3" />
            </IconButton>
          ) : null}
        </div>

        <div // NOSONAR -- combobox listbox; a native select cannot host the search field
          aria-multiselectable={multiple || undefined}
          className="relative max-h-[220px] overflow-y-auto rounded-8 border border-gray-5 bg-gray-1 py-1"
          id={listId}
          onMouseMove={handleMouseMove}
          ref={listRef}
          role="listbox"
        >
          {visible.length > 0 ? (
            <div className="flex flex-col px-1">
              {visible.map((option) => (
                <WorkspaceAgentFilterOption
                  domId={rowDomId(option.index)}
                  index={option.index}
                  isHighlighted={option.index === highlighted?.index}
                  isKeyboardHighlighted={isKeyboardHighlight && option.index === highlighted?.index}
                  key={option.index}
                  label={option.label}
                  multiple={multiple}
                  onHighlight={handleHighlight}
                  onSelect={onSelect}
                  selected={selectedIndexes.includes(option.index)}
                />
              ))}
            </div>
          ) : (
            <p className="px-3 py-6 text-center text-12 text-gray-9 leading-4">No results</p>
          )}
        </div>
      </div>
    );
  },
);

WorkspaceAgentFilterList.displayName = 'WorkspaceAgentFilterList';
